import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import {
  BuildingOffice2Icon,
  PlusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronRightIcon,
  UserGroupIcon,
  CircleStackIcon,
  ChartBarIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

export default function Companies() {
  const navigate = useNavigate();
  const { selectedCompany, selectCompany, clearSelectedCompany, isSuperUser } = useAuthStore();
  
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    industry: '',
    billingPlan: 'starter'
  });
  
  // Check if user is authorized
  useEffect(() => {
    if (!isSuperUser()) {
      navigate('/dashboards');
      toast.error('Access denied. Super Admin or Managing Admin required.');
    }
  }, [isSuperUser, navigate]);
  
  // Fetch companies
  useEffect(() => {
    fetchCompanies();
  }, []);
  
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/companies');
      setCompanies(response.data.data || response.data.companies || []);
    } catch (error) {
      console.error('Fetch companies error:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    try {
      setCreating(true);
      const response = await api.post('/companies', newCompany);
      setCompanies([response.data.company, ...companies]);
      setShowCreateModal(false);
      setNewCompany({ name: '', industry: '', billingPlan: 'starter' });
      toast.success('Company created successfully');
    } catch (error) {
      console.error('Create company error:', error);
      toast.error(error.response?.data?.error || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  };
  
  const handleSelectCompany = (company) => {
    selectCompany(company);
    toast.success(`Now working as ${company.name}`);
  };
  
  const handleClearSelection = () => {
    clearSelectedCompany();
    toast.success('Cleared company selection');
  };
  
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(search.toLowerCase()) ||
    company.industry?.toLowerCase().includes(search.toLowerCase())
  );
  
  const billingPlans = [
    { value: 'starter', label: 'Starter', description: 'Up to 5 users, 10GB storage' },
    { value: 'professional', label: 'Professional', description: 'Up to 25 users, 100GB storage' },
    { value: 'enterprise', label: 'Enterprise', description: 'Unlimited users, 1TB storage' }
  ];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900 dark:text-white">
            Companies
          </h1>
          <p className="text-surface-600 dark:text-surface-400 mt-1">
            Manage and switch between companies
          </p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <PlusIcon className="w-5 h-5" />
          New Company
        </button>
      </div>
      
      {/* Current Selection Banner */}
      {selectedCompany && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-800 flex items-center justify-center">
              <BuildingOffice2Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-primary-600 dark:text-primary-400">Currently working as</p>
              <p className="font-semibold text-primary-900 dark:text-primary-100">{selectedCompany.name}</p>
            </div>
          </div>
          <button
            onClick={handleClearSelection}
            className="btn-ghost text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"
          >
            <XCircleIcon className="w-5 h-5 mr-1" />
            Clear Selection
          </button>
        </div>
      )}
      
      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search companies..."
          className="input pl-10"
        />
      </div>
      
      {/* Companies Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="text-center py-12">
          <BuildingOffice2Icon className="w-12 h-12 text-surface-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            {search ? 'No companies found' : 'No companies yet'}
          </h3>
          <p className="text-surface-600 dark:text-surface-400 mb-4">
            {search ? 'Try a different search term' : 'Create your first company to get started'}
          </p>
          {!search && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              <PlusIcon className="w-5 h-5" />
              Create Company
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCompanies.map((company) => {
            const isSelected = selectedCompany?._id === company._id;
            
            return (
              <div
                key={company._id}
                className={`card hover:shadow-lg transition-all cursor-pointer ${
                  isSelected 
                    ? 'ring-2 ring-primary-500 border-primary-500' 
                    : 'hover:border-surface-400 dark:hover:border-surface-600'
                }`}
                onClick={() => handleSelectCompany(company)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isSelected 
                        ? 'bg-primary-100 dark:bg-primary-900' 
                        : 'bg-surface-100 dark:bg-surface-800'
                    }`}>
                      {company.logo ? (
                        <img src={company.logo} alt={company.name} className="w-8 h-8 rounded" />
                      ) : (
                        <BuildingOffice2Icon className={`w-6 h-6 ${
                          isSelected 
                            ? 'text-primary-600 dark:text-primary-400' 
                            : 'text-surface-600 dark:text-surface-400'
                        }`} />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-surface-900 dark:text-white">
                        {company.name}
                      </h3>
                      {company.industry && (
                        <p className="text-sm text-surface-500 dark:text-surface-400">
                          {company.industry}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <CheckIcon className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="text-center p-2 bg-surface-50 dark:bg-surface-800 rounded-lg">
                    <UserGroupIcon className="w-4 h-4 text-surface-400 mx-auto mb-1" />
                    <p className="text-sm font-medium text-surface-900 dark:text-white">
                      {company.currentUsage?.seats || 0}
                    </p>
                    <p className="text-xs text-surface-500">Users</p>
                  </div>
                  <div className="text-center p-2 bg-surface-50 dark:bg-surface-800 rounded-lg">
                    <ChartBarIcon className="w-4 h-4 text-surface-400 mx-auto mb-1" />
                    <p className="text-sm font-medium text-surface-900 dark:text-white">
                      {company.currentUsage?.dashboards || 0}
                    </p>
                    <p className="text-xs text-surface-500">Dashboards</p>
                  </div>
                  <div className="text-center p-2 bg-surface-50 dark:bg-surface-800 rounded-lg">
                    <CircleStackIcon className="w-4 h-4 text-surface-400 mx-auto mb-1" />
                    <p className="text-sm font-medium text-surface-900 dark:text-white">
                      {company.currentUsage?.dataSources || 0}
                    </p>
                    <p className="text-xs text-surface-500">Sources</p>
                  </div>
                </div>
                
                {/* Status & Plan */}
                <div className="flex items-center justify-between pt-3 border-t border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-2">
                    {company.isActive ? (
                      <span className="badge-success">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="badge-danger">
                        <XCircleIcon className="w-3 h-3 mr-1" />
                        Inactive
                      </span>
                    )}
                    <span className="badge-primary capitalize">
                      {company.billingPlan || 'Starter'}
                    </span>
                  </div>
                  
                  <ChevronRightIcon className="w-5 h-5 text-surface-400" />
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-900 rounded-xl w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-surface-200 dark:border-surface-700">
              <h2 className="text-xl font-semibold text-surface-900 dark:text-white">
                Create New Company
              </h2>
              <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                Set up a new company account
              </p>
            </div>
            
            <form onSubmit={handleCreateCompany} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                  className="input"
                  placeholder="Acme Corporation"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Industry
                </label>
                <input
                  type="text"
                  value={newCompany.industry}
                  onChange={(e) => setNewCompany({ ...newCompany, industry: e.target.value })}
                  className="input"
                  placeholder="Technology, Healthcare, etc."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Billing Plan
                </label>
                <div className="space-y-2">
                  {billingPlans.map((plan) => (
                    <label
                      key={plan.value}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        newCompany.billingPlan === plan.value
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-surface-200 dark:border-surface-700 hover:border-surface-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="billingPlan"
                        value={plan.value}
                        checked={newCompany.billingPlan === plan.value}
                        onChange={(e) => setNewCompany({ ...newCompany, billingPlan: e.target.value })}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                        newCompany.billingPlan === plan.value
                          ? 'border-primary-500'
                          : 'border-surface-400'
                      }`}>
                        {newCompany.billingPlan === plan.value && (
                          <div className="w-2 h-2 rounded-full bg-primary-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-surface-900 dark:text-white">{plan.label}</p>
                        <p className="text-sm text-surface-500 dark:text-surface-400">{plan.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewCompany({ name: '', industry: '', billingPlan: 'starter' });
                  }}
                  className="btn-secondary flex-1"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-5 h-5" />
                      Create Company
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

